/**
 * FogOverlay — soft, antialiased fog of war.
 *
 * Earlier versions drew one sharp rectangle per sim cell straight onto a
 * Phaser.Graphics object. With a 32-px sim cell over a 2000×2000 world that
 * produced ~4 000 hard-edged rectangles and a very pixelated look. We now
 * rasterise the fog into an off-screen Canvas at sim-cell resolution
 * (cellsX × cellsY pixels), run a tiny box-blur pass to soften edges, then
 * display the result as a Phaser Image scaled up to world size with bilinear
 * filtering. The texture is updated only when MatchState.fog changes
 * (per-tick, not per-frame).
 *
 * Per docs/02_GAME_DESIGN.md §5:
 *   - Visible cells: clear (no overlay).
 *   - Explored cells: dim grayscale (last-seen reading is allowed for static
 *     entities — Cores, buildings, control nodes are still drawn underneath).
 *   - Unexplored cells: heavy dark cover (map shape only).
 *
 * Drawn at depth 15 so it sits above buildings/squads but below FX (depth 20).
 */

import type { FogGrid, MatchState, PlayerId } from '@signalclash/shared';
import { FOG_EXPLORED, FOG_VISIBLE } from '@signalclash/shared';
import Phaser from 'phaser';

const TEX_KEY_PREFIX = 'fog_overlay_';
const ALPHA_EXPLORED = 0.50;
const ALPHA_UNEXPLORED = 0.82;

export class FogOverlay {
  private readonly scene: Phaser.Scene;
  private readonly texKey: string;
  private viewerId: PlayerId;
  private dirty = true;
  private lastFog: FogGrid | null = null;
  private tex: Phaser.Textures.CanvasTexture | null = null;
  private img: Phaser.GameObjects.Image | null = null;
  // Re-usable Uint8 buffer sized to cellsX*cellsY; allocated lazily.
  private alphaBuf: Uint8ClampedArray | null = null;
  private bufCellsX = 0;
  private bufCellsY = 0;

  constructor(scene: Phaser.Scene, viewerId: PlayerId) {
    this.scene = scene;
    this.viewerId = viewerId;
    // Per-viewer key so a renderer re-using the overlay on viewer-flip
    // doesn't collide with a leftover texture.
    this.texKey = TEX_KEY_PREFIX + Math.floor(Math.random() * 1e9).toString(36);
  }

  setViewerId(viewerId: PlayerId): void {
    this.viewerId = viewerId;
    this.dirty = true;
    this.lastFog = null;
  }

  dispose(): void {
    this.img?.destroy();
    this.img = null;
    if (this.tex !== null && this.scene.textures.exists(this.texKey)) {
      this.scene.textures.remove(this.texKey);
    }
    this.tex = null;
  }

  observeSnapshot(state: MatchState): void {
    const grid = state.fog[this.viewerId];
    if (this.lastFog !== grid) {
      this.lastFog = grid;
    }
    // Mark dirty unconditionally — snapshot rate is sim tick rate, not frame
    // rate, so cost is bounded. Same-reference grids may still have mutated
    // cells when the snapshot path doesn't deep-clone.
    this.dirty = true;
  }

  redrawIfDirty(): void {
    if (!this.dirty) return;
    if (this.lastFog === null) return;
    this.repaint(this.lastFog);
    this.dirty = false;
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------
  private ensureTexture(grid: FogGrid): Phaser.Textures.CanvasTexture | null {
    if (this.tex !== null && this.bufCellsX === grid.cellsX && this.bufCellsY === grid.cellsY) {
      return this.tex;
    }
    // Map size or first paint: (re-)create the canvas texture sized to the
    // sim cell grid. Bilinear filter on display gives soft fog edges.
    if (this.tex !== null && this.scene.textures.exists(this.texKey)) {
      this.scene.textures.remove(this.texKey);
    }
    const t = this.scene.textures.createCanvas(this.texKey, grid.cellsX, grid.cellsY);
    if (t === null) {
      // iOS / low-memory devices can refuse the canvas allocation under
      // pressure. Throwing here used to cascade out through the
      // snapshot-pump and kill the render loop; we'd rather skip the
      // fog draw for this frame and let the player continue — fog
      // off is a far better failure mode than a black-screen freeze.
      // eslint-disable-next-line no-console
      console.warn('[fog] createCanvas returned null; fog overlay disabled');
      return null;
    }
    this.tex = t;
    this.bufCellsX = grid.cellsX;
    this.bufCellsY = grid.cellsY;
    this.alphaBuf = new Uint8ClampedArray(grid.cellsX * grid.cellsY);
    // (Re-)attach the display Image with bilinear filtering.
    this.img?.destroy();
    const worldW = grid.cellsX * grid.tilePx;
    const worldH = grid.cellsY * grid.tilePx;
    const img = this.scene.add.image(worldW / 2, worldH / 2, this.texKey);
    img.setOrigin(0.5, 0.5);
    img.setDisplaySize(worldW, worldH);
    img.setDepth(15);
    // LINEAR filter on the texture gives smooth bilinear upscale (instead of
    // crisp NEAREST). The visible texture is now soft-edged.
    t.setFilter(Phaser.Textures.FilterMode.LINEAR);
    this.img = img;
    return t;
  }

  private repaint(grid: FogGrid): void {
    const tex = this.ensureTexture(grid);
    if (tex === null) return;
    const buf = this.alphaBuf;
    if (buf === null) return;
    // First pass: write the raw alpha value (0/EXPLORED/UNEXPLORED).
    for (let cy = 0; cy < grid.cellsY; cy++) {
      const rowStart = cy * grid.cellsX;
      for (let cx = 0; cx < grid.cellsX; cx++) {
        const v = grid.cells[rowStart + cx] ?? 0;
        const a = v === FOG_VISIBLE ? 0 : v === FOG_EXPLORED ? Math.round(ALPHA_EXPLORED * 255) : Math.round(ALPHA_UNEXPLORED * 255);
        buf[rowStart + cx] = a;
      }
    }
    // Second pass: 3×3 box blur (single iteration) — softens vision/no-vision
    // boundaries so the bilinear upscale doesn't leak hard step edges. Bounded
    // O(cellsX × cellsY) ≈ 4 000 ops on a 2000-px map; ~µs work each tick.
    const blurred = new Uint8ClampedArray(buf.length);
    const W = grid.cellsX, H = grid.cellsY;
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        let sum = 0, n = 0;
        for (let dy = -1; dy <= 1; dy++) {
          const yy = y + dy;
          if (yy < 0 || yy >= H) continue;
          for (let dx = -1; dx <= 1; dx++) {
            const xx = x + dx;
            if (xx < 0 || xx >= W) continue;
            sum += buf[yy * W + xx] ?? 0;
            n++;
          }
        }
        blurred[y * W + x] = Math.round(sum / n);
      }
    }
    // Final pass: write into the canvas ImageData. Most fog cells get a
    // cold near-black RGB (atmospheric vs flat grey). At the vision-edge band
    // — cells where the blurred alpha is partial, i.e. fading from clear to
    // deep fog — blend toward the viewer's team color. This draws a soft
    // illuminated rim around what the viewer can see, the way an RTS
    // "your forces light the world" feels (StarCraft 2, Iron Harvest).
    const viewerP0 = this.viewerId === 0;
    // V1.8.1 dial-back — initial tint was too saturated and bled across the
    // whole visible zone. Chroma cut roughly in half, weight cap reduced
    // (0.65 → 0.30), edge band narrowed (30..200 → 60..170) so the tint
    // only shows in a thin rim right at the vision frontier.
    const tintR = viewerP0 ?  14 :  60;
    const tintG = viewerP0 ?  30 :  28;
    const tintB = viewerP0 ?  60 :  18;
    const baseR = 5, baseG = 8, baseB = 14;
    const ctx = tex.context;
    const id = ctx.createImageData(W, H);
    const data = id.data;
    for (let i = 0; i < blurred.length; i++) {
      const j = i * 4;
      const a = blurred[i] ?? 0;
      let r = baseR, g = baseG, b = baseB;
      if (a > 60 && a < 170) {
        const t = Math.max(0, 1 - Math.abs(a - 115) / 55) * 0.30;
        r = Math.round(baseR + (tintR - baseR) * t);
        g = Math.round(baseG + (tintG - baseG) * t);
        b = Math.round(baseB + (tintB - baseB) * t);
      }
      data[j] = r;
      data[j + 1] = g;
      data[j + 2] = b;
      data[j + 3] = a;
    }
    ctx.putImageData(id, 0, 0);
    tex.refresh();
  }
}
